import { useState, useRef, useEffect, useMemo, useCallback, Component, lazy, Suspense, type ReactNode, type ErrorInfo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
// react-resizable-panels removed — using simple flex layout with drag handle
import { fetchConversation, fetchMessages } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import { formatTime } from '../../hooks/useTimeAgo'
import { useAwakePersonas, isAwake as checkAwake } from '../../hooks/useAwakePersonas'
import MagicBox from '../../components/MagicBox'
import ChatterPlayer from '../../components/ChatterPlayer'
import ChatterBox from '../../components/ChatterBox'
const RoundTableDashboard = lazy(() => import('../../components/RoundTableDashboard'))
const ProjectBrowser = lazy(() => import('../../components/ProjectBrowser'))
const PresentationBrowser = lazy(() => import('../../components/PresentationBrowser'))
import RightPanelTabs from '../../components/RightPanelTabs'
import { useAuth } from '../../context/AuthContext'
import { useSSEConnected } from '../../hooks/useSSE'
import { usePersonaPopup } from '../../components/PersonaPopup'
import type { Message, Conversation, Participant } from '../../lib/types'

let _flashInterval: ReturnType<typeof setInterval> | null = null
let _originalTitle = ''
function flashTabTitle(count: number) {
  if (document.hasFocus()) return
  if (!_originalTitle) _originalTitle = document.title
  if (_flashInterval) clearInterval(_flashInterval)
  let on = false
  _flashInterval = setInterval(() => {
    document.title = on ? _originalTitle : `💬 ${count} new message${count > 1 ? 's' : ''}`
    on = !on
  }, 1000)
  const stopFlash = () => {
    if (_flashInterval) { clearInterval(_flashInterval); _flashInterval = null }
    document.title = _originalTitle || document.title
    _originalTitle = ''
    window.removeEventListener('focus', stopFlash)
  }
  window.addEventListener('focus', stopFlash)
}

// ─── Ceremony constants ────────────────────────────────────────
const CEREMONY_ACCENT = '#D4A054'
const CEREMONY_PHASES = ['Preparing', 'Intake', 'Assessment', 'Architecture', 'Identity', 'Integration'] as const

type ContentType = 'text' | 'image' | 'html' | 'video' | 'audio'

interface MagicBoxItem {
  id: number
  content_type: ContentType
  title?: string
  content: string
  posted_by?: string
  created_at: string
}

// ─── Ceremony Phase Indicator ──────────────────────────────────
function CeremonyPhaseBar({ currentPhase = 'Preparing' }: { currentPhase?: string }) {
  const activeIdx = CEREMONY_PHASES.indexOf(currentPhase as typeof CEREMONY_PHASES[number])
  return (
    <div
      className="shrink-0 px-5 py-2 flex items-center justify-center gap-0"
      style={{
        background: 'rgba(212,160,84,0.04)',
        borderBottom: '1px solid rgba(212,160,84,0.12)',
      }}
    >
      {CEREMONY_PHASES.map((phase, i) => {
        const isActive = i === activeIdx
        const isPast = i < activeIdx
        const dotColor = isActive ? CEREMONY_ACCENT : isPast ? 'rgba(212,160,84,0.5)' : 'rgba(255,255,255,0.15)'
        const labelColor = isActive ? CEREMONY_ACCENT : isPast ? 'rgba(212,160,84,0.5)' : 'var(--text-muted)'
        return (
          <div key={phase} className="flex items-center">
            {i > 0 && (
              <div
                style={{
                  width: 32,
                  height: 1,
                  background: isPast ? 'rgba(212,160,84,0.4)' : 'rgba(255,255,255,0.08)',
                }}
              />
            )}
            <div className="flex flex-col items-center gap-1" style={{ minWidth: 64 }}>
              <div
                style={{
                  width: isActive ? 8 : 6,
                  height: isActive ? 8 : 6,
                  borderRadius: '50%',
                  background: dotColor,
                  boxShadow: isActive ? `0 0 8px ${CEREMONY_ACCENT}60` : 'none',
                  transition: 'all 0.3s ease',
                }}
              />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: isActive ? 600 : 400,
                  color: labelColor,
                  letterSpacing: 0.3,
                  transition: 'all 0.3s ease',
                }}
              >
                {phase}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Ceremony Stage (left panel) ───────────────────────────────
function CeremonyStage({
  convo,
  participants,
  magicBoxItems,
  awakeSet,
}: {
  convo: Conversation & { metadata_special?: string }
  participants: Participant[]
  magicBoxItems: MagicBoxItem[]
  awakeSet: Set<string>
}) {
  const [selectedIdx, setSelectedIdx] = useState(magicBoxItems.length - 1)
  // Auto-select newest item when new items arrive
  useEffect(() => {
    if (magicBoxItems.length > 0) setSelectedIdx(magicBoxItems.length - 1)
  }, [magicBoxItems.length])

  const currentItem = magicBoxItems.length > 0 ? magicBoxItems[Math.min(selectedIdx, magicBoxItems.length - 1)] : null

  if (currentItem) {
    // Render magic box content in the stage
    return (
      <div className="h-full flex flex-col p-6 overflow-hidden">
        <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center">
          {currentItem.title && (
            <h2
              className="text-lg font-semibold mb-4 text-center"
              style={{ color: CEREMONY_ACCENT }}
            >
              {currentItem.title}
            </h2>
          )}
          <div className="w-full max-w-md">
            {currentItem.content_type === 'image' ? (
              <img src={currentItem.content} alt={currentItem.title || ''} className="rounded-lg w-full" style={{ objectFit: 'contain' }} />
            ) : currentItem.content_type === 'html' ? (
              <div dangerouslySetInnerHTML={{ __html: currentItem.content }} />
            ) : (
              <div className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                <MarkdownRenderer content={currentItem.content} />
              </div>
            )}
          </div>
        </div>
        {magicBoxItems.length > 1 && (
          <div className="shrink-0 flex flex-wrap gap-2 pt-4 mt-2" style={{ borderTop: '1px solid rgba(212,160,84,0.15)' }}>
            {magicBoxItems.map((item, i) => {
              let label = item.title || ''
              if (!label && item.content_type === 'image') label = 'Image'
              if (!label && item.content_type === 'html') {
                const match = item.content.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/i)
                label = match ? match[1].replace(/<[^>]*>/g, '') : item.content.replace(/<[^>]*>/g, '').substring(0, 30)
              }
              if (!label) label = item.content.substring(0, 30)
              if (label.length > 28) label = label.substring(0, 28) + '...'
              const active = i === Math.min(selectedIdx, magicBoxItems.length - 1)
              return (
                <div
                  key={item.id}
                  onClick={() => setSelectedIdx(i)}
                  style={{
                    padding: '4px 10px',
                    background: active ? 'rgba(212,160,84,0.15)' : 'rgba(255,255,255,0.05)',
                    border: active ? `1px solid ${CEREMONY_ACCENT}` : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 6,
                    fontSize: 11,
                    color: active ? CEREMONY_ACCENT : 'var(--text-muted)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // Welcome state
  return (
    <div className="h-full flex flex-col items-center justify-center p-8 overflow-y-auto">
      {/* Title */}
      <h2
        className="text-2xl font-bold mb-3 text-center"
        style={{ color: CEREMONY_ACCENT, letterSpacing: 0.5 }}
      >
        {convo.title || 'Ceremony Room'}
      </h2>

      {/* Description */}
      <p
        className="text-sm leading-relaxed text-center mb-8"
        style={{ color: 'var(--text-secondary)', maxWidth: 400 }}
      >
        {convo.metadata_special || 'A sacred space for discovery. Through guided dialogue, identity emerges.'}
      </p>

      {/* Participants */}
      <div className="w-full max-w-xs">
        <div className="text-xs font-medium uppercase tracking-wider mb-3 text-center" style={{ color: 'var(--text-muted)' }}>
          Participants
        </div>
        <div className="flex flex-col gap-2">
          {participants.map(p => {
            const uid = p.user_id ?? p.persona
            const isLead = uid === convo.metadata_lead
            const isGlean = uid.toLowerCase() === 'glean'
            const role = isLead ? 'Guide' : isGlean ? 'Witness' : 'Participant'
            const roleColor = isLead ? CEREMONY_ACCENT : 'var(--text-muted)'
            return (
              <div
                key={uid}
                className="flex items-center gap-3 px-3 py-2 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
              >
                <PersonaAvatar name={uid} size={28} showStatus awake={checkAwake(awakeSet, uid)} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium" style={{ color: senderColor(p.persona || uid) }}>
                    {displayName(p.persona || uid)}
                  </span>
                </div>
                <span className="text-xs" style={{ color: roleColor, fontWeight: isLead ? 600 : 400 }}>
                  {role}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Status badge */}
      <div
        className="mt-8 px-4 py-1.5 rounded-full text-xs font-medium"
        style={{
          background: `${CEREMONY_ACCENT}15`,
          color: CEREMONY_ACCENT,
          border: `1px solid ${CEREMONY_ACCENT}30`,
          letterSpacing: 0.5,
        }}
      >
        Preparing
      </div>
    </div>
  )
}

// ─── Error Boundary for MagicBox ────────────────────────────────
class MagicBoxErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(_error: Error, _info: ErrorInfo) { /* silently catch */ }
  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}

// ─── Workspace constants ───────────────────────────────────────
const WORKSPACE_ACCENT = '#58A6FF'

interface ProjectTask {
  id: string
  title: string
  status: string
  assignee?: string
  priority?: string
}

function WorkspaceSidebar({ projectData, awakeSet }: {
  projectData: { title?: string; description?: string; tasks?: ProjectTask[]; lead?: string; status?: string } | null
  awakeSet: Set<string>
}) {
  if (!projectData) {
    return (
      <div
        style={{
          width: '30%',
          flexShrink: 0,
          borderRight: '1px solid var(--border)',
          padding: 16,
          overflowY: 'auto',
          background: 'rgba(88,166,255,0.02)',
        }}
      >
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>No project linked</div>
      </div>
    )
  }

  const tasks = projectData.tasks || []
  const completed = tasks.filter(t => t.status === 'completed').length
  const total = tasks.length
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0

  return (
    <div
      style={{
        width: '30%',
        flexShrink: 0,
        borderRight: '1px solid var(--border)',
        overflowY: 'auto',
        background: 'rgba(88,166,255,0.02)',
      }}
    >
      {/* Project header */}
      <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--border)' }}>
        <h3 className="text-sm font-semibold mb-1" style={{ color: WORKSPACE_ACCENT }}>
          {projectData.title || 'Project'}
        </h3>
        {projectData.lead && (
          <div className="flex items-center gap-1.5 mb-2">
            <PersonaAvatar name={projectData.lead} size={14} showStatus awake={checkAwake(awakeSet, projectData.lead)} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Lead</span>
          </div>
        )}
        {/* Progress bar */}
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 4, height: 4, marginTop: 8 }}>
          <div style={{ width: `${progress}%`, background: WORKSPACE_ACCENT, borderRadius: 4, height: '100%', transition: 'width 0.3s ease' }} />
        </div>
        <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          {completed}/{total} tasks ({progress}%)
        </div>
      </div>

      {/* Task list */}
      <div style={{ padding: '8px 12px' }}>
        {tasks.map(task => {
          const isDone = task.status === 'completed'
          const isActive = task.status === 'in_progress'
          return (
            <div
              key={task.id}
              style={{
                padding: '6px 8px',
                borderRadius: 6,
                marginBottom: 2,
                background: isActive ? 'rgba(88,166,255,0.08)' : 'transparent',
                opacity: isDone ? 0.5 : 1,
              }}
            >
              <div className="flex items-center gap-2">
                <span style={{ fontSize: 10, color: isDone ? '#3fb950' : isActive ? WORKSPACE_ACCENT : 'var(--text-muted)' }}>
                  {isDone ? '✓' : isActive ? '●' : '○'}
                </span>
                <span className="text-xs flex-1 truncate" style={{ color: 'var(--text-primary)', textDecoration: isDone ? 'line-through' : 'none' }}>
                  {task.title}
                </span>
                {task.assignee && (
                  <PersonaAvatar name={task.assignee} size={14} showStatus awake={checkAwake(awakeSet, task.assignee)} />
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Persona colors (same as PersonaAvatar) ────────────────────
const PERSONA_COLORS: Record<string, string> = {
  forge: '#B25943', mira: '#9C59F5', flux: '#33C7A0', chronicle: '#8C6DBF',
  timbre: '#ED9429', griot: '#4E937A', relay: '#4493D4', sentinel: '#999999',
  cairn: '#7A9E7E', joel: '#59C5FA', glean: '#A89060', wrench: '#8B8B8B',
  ember: '#E06040', lumen: '#F5D623', vector: '#5AA0D0', ledger: '#6B8E70',
  beacon: '#D4A520', gretchen: '#B87DB8', vigil: '#708090', herald: '#C0785A',
  grain: '#A5886F', loom: '#7B6FA0', noma: '#E8A87C', counsel: '#6B7B8D',
  pulse: '#60A0B0', cadence: '#9B7A6C', anvil: '#7D7D7D', rivet: '#8C9440',
  system: '#484f58',
}

function senderColor(name: string): string {
  return PERSONA_COLORS[name?.toLowerCase()] || '#8b949e'
}

// ─── Display names ─────────────────────────────────────────────
// Known user ID → display name map (for participant chips)
// Dynamic display name resolution — built from conversation participants + persona list
// No hardcoded PU-number map. Participants carry their own display_name.
const _displayNameCache: Record<string, string> = { 'joel': 'Joel', 'svc-jitsi-bridge': 'Jitsi Bridge' }

function buildDisplayNameCache(participants: Participant[]) {
  for (const p of participants) {
    // Only cache display_name if it's a real name, not a raw PU-number
    const hasGoodDisplayName = p.display_name && !p.display_name.match(/^pu-\d/)
    if (p.user_id && hasGoodDisplayName) {
      _displayNameCache[p.user_id] = p.display_name!
    }
    if (p.persona) {
      _displayNameCache[p.persona] = (hasGoodDisplayName ? p.display_name : p.persona)!
      _displayNameCache[p.persona.toLowerCase()] = (hasGoodDisplayName ? p.display_name : p.persona)!
    }
  }
}

function displayName(name: string, fallbackDisplayName?: string): string {
  if (!name) return fallbackDisplayName || '?'
  // Check cache (built from participants)
  if (_displayNameCache[name]) return _displayNameCache[name]
  if (_displayNameCache[name.toLowerCase()]) return _displayNameCache[name.toLowerCase()]
  // Strip pu- prefix if it looks like a short persona name
  if (name.startsWith('pu-') && name.length < 15) return name.slice(3)[0].toUpperCase() + name.slice(4)
  // Long pu- IDs: prefer participant's own display_name if server provided one
  if (name.startsWith('pu-') && name.length > 15) {
    if (fallbackDisplayName && !fallbackDisplayName.startsWith('pu-')) {
      return fallbackDisplayName.charAt(0).toUpperCase() + fallbackDisplayName.slice(1)
    }
    return 'Unknown'
  }
  // Default: capitalize
  return name[0].toUpperCase() + name.slice(1)
}

// ─── Parse UTC timestamps ──────────────────────────────────────
function parseUTC(dateStr: string): Date {
  if (dateStr.includes('Z') || dateStr.includes('+') || dateStr.includes('T')) return new Date(dateStr)
  return new Date(dateStr.replace(' ', 'T') + 'Z')
}

// ─── Smart day labels ──────────────────────────────────────────
function dayLabel(dateStr: string): string {
  const date = parseUTC(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)
  const eventDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())

  if (eventDay.getTime() === today.getTime()) return 'Today'
  if (eventDay.getTime() === yesterday.getTime()) return 'Yesterday'
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

// ─── Message grouping — consecutive same-sender within 5 min ───
interface GroupedMessage extends Message {
  showHeader: boolean
  showAvatar: boolean
  dayDivider?: string
}

function groupMessages(messages: Message[]): GroupedMessage[] {
  const result: GroupedMessage[] = []
  let lastSender = ''
  let lastTime = 0
  let lastDay = ''

  for (const msg of messages) {
    const day = dayLabel(msg.created_at)
    const msgTime = parseUTC(msg.created_at).getTime()
    const sameGroup = msg.sender === lastSender && !msg.is_system && (msgTime - lastTime) < 300000 // 5 min
    const isNewDay = day !== lastDay

    result.push({
      ...msg,
      showHeader: isNewDay || !sameGroup || !!msg.is_system,
      showAvatar: isNewDay || !sameGroup || !!msg.is_system,
      dayDivider: isNewDay ? day : undefined,
    })

    lastSender = msg.is_system ? '' : msg.sender
    lastTime = msgTime
    lastDay = day
  }

  return result
}

// ─── Send message ──────────────────────────────────────────────
async function sendMessage(convId: string, content: string, senderName: string, chatter?: string) {
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const body: Record<string, string> = { content, sender: senderName }
  if (chatter) body.chatter = chatter
  const res = await fetch(`/api/conversations/${convId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`${res.status}: ${body || res.statusText}`)
  }
  return res.json()
}

// ═══════════════════════════════════════════════════════════════
// Settings Panel
// ═══════════════════════════════════════════════════════════════

function SettingsPanel({ convo, convId, participants, senderId, onClose, onSave }: {
  convo: Conversation | undefined
  convId: string
  participants: Participant[]
  senderId: string
  onClose: () => void
  onSave: () => void
}) {
  const [title, setTitle] = useState(convo?.title || '')
  const [visibility, setVisibility] = useState(convo?.visibility || 'open')
  const [roomType, setRoomType] = useState(convo?.room_type || 'chat')
  const [lead, setLead] = useState(convo?.metadata_lead || '')
  const [protocol, setProtocol] = useState(convo?.metadata_protocol || 'open-floor')
  const [chatter, setChatter] = useState(!!convo?.metadata_chatter)
  const [special, setSpecial] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [addName, setAddName] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const [allPersonas, setAllPersonas] = useState<{ name: string; user_id: string }[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  const adminIds: string[] = (() => {
    try { return JSON.parse(convo?.admin_ids || '[]') } catch { return [] }
  })()
  const isAdmin = adminIds.length === 0 || adminIds.includes(senderId)

  useEffect(() => {
    const tkn = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1] || ''
    fetch('/api/personas', { headers: tkn ? { Authorization: `Bearer ${tkn}` } : {} }).then(r => r.json()).then(data => {
      const list = (Array.isArray(data) ? data : data.personas || []).map((p: any) => ({
        name: p.display_name || p.name || p.id,
        user_id: p.user_id || `pu-${p.name || p.id}`,
      }))
      // Add Joel
      if (!list.find((p: any) => p.name === 'Joel')) list.unshift({ name: 'Joel', user_id: 'joel' })
      setAllPersonas(list)
    }).catch(() => {})
  }, [])

  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const authHeaders: Record<string, string> = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`/api/conversations/${convId}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({
          title: title.trim() || null,
          visibility,
          room_type: roomType,
          metadata_lead: lead || null,
          metadata_protocol: protocol,
          metadata_chatter: chatter ? 1 : 0,
          metadata_special: special || null,
        }),
      })
      if (!res.ok) {
        setError(`Save failed (${res.status}) — nothing was changed. Try again.`)
        setSaving(false)
        return
      }
    } catch {
      setError('Save failed (network) — nothing was changed. Try again.')
      setSaving(false)
      return
    }
    setSaving(false)
    onSave()
  }

  async function handleAddMember(userId: string) {
    setAddLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/conversations/${convId}/add-member`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ user_id: userId, actor_id: senderId }),
      })
      if (!res.ok) {
        setError(`Add member failed (${res.status}) — not added. Try again.`)
        setAddLoading(false)
        return
      }
    } catch {
      setError('Add member failed (network) — not added. Try again.')
      setAddLoading(false)
      return
    }
    setAddName('')
    setShowSuggestions(false)
    setAddLoading(false)
    onSave()
  }

  async function handleRemoveMember(userId: string) {
    setError(null)
    try {
      const res = await fetch(`/api/conversations/${convId}/remove-member`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ user_id: userId, actor_id: senderId }),
      })
      if (!res.ok) {
        setError(`Remove member failed (${res.status}) — not removed. Try again.`)
        return
      }
    } catch {
      setError('Remove member failed (network) — not removed. Try again.')
      return
    }
    onSave()
  }

  async function handlePromoteAdmin(userId: string) {
    setError(null)
    try {
      const res = await fetch(`/api/conversations/${convId}/promote-admin`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ user_id: userId, actor_id: senderId }),
      })
      if (!res.ok) {
        setError(`Promote failed (${res.status}) — not promoted. Try again.`)
        return
      }
    } catch {
      setError('Promote failed (network) — not promoted. Try again.')
      return
    }
    onSave()
  }

  const participantIds = new Set(participants.map(p => p.user_id))
  const suggestions = allPersonas.filter(p =>
    !participantIds.has(p.user_id) &&
    p.name.toLowerCase().includes(addName.toLowerCase())
  ).slice(0, 8)

  const inputStyle = {
    width: '100%', background: 'var(--bg-body)', border: '1px solid var(--border)',
    borderRadius: 4, padding: '6px 8px', color: 'var(--text-primary)', fontSize: 12, outline: 'none',
  }
  const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', display: 'block' as const, marginBottom: 4 }

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div
        className="fixed z-50 rounded-xl p-5 overflow-y-auto"
        style={{
          top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          minWidth: 380, maxWidth: '90vw', maxHeight: '85vh',
        }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Conversation Settings</h3>

        <label style={labelStyle}>Title</label>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Auto-generated from participants..." style={{ ...inputStyle, marginBottom: 12 }} />

        <label style={labelStyle}>Visibility</label>
        <select value={visibility} onChange={e => setVisibility(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }}>
          <option value="open">Open (all community users)</option>
          <option value="participants">Participants only</option>
          <option value="custom">Custom (participants + allowed list)</option>
        </select>

        <label style={labelStyle}>Room Type</label>
        <select value={roomType} onChange={e => setRoomType(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }}>
          <option value="chat">Chat</option>
          <option value="ceremony">Ceremony</option>
          <option value="workspace">Workspace</option>
          <option value="round-table">Round Table</option>
          <option value="3d">3D</option>
        </select>

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '12px 0' }} />
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Participants</h3>

        {/* Current participants */}
        <div style={{ marginBottom: 12 }}>
          {participants.map(p => {
            const uid = p.user_id || p.persona
            const isParticipantAdmin = adminIds.includes(uid)
            return (
              <div key={uid} className="flex items-center justify-between py-1.5 px-2 rounded" style={{ marginBottom: 2, background: 'var(--bg-body)' }}>
                <div className="flex items-center gap-2">
                  <PersonaAvatar name={uid} size={22} />
                  <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{displayName(p.persona || uid, p.display_name)}</span>
                  {isParticipantAdmin && (
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(210,153,34,0.15)', color: '#d29922', fontSize: 10 }}>Admin</span>
                  )}
                </div>
                {isAdmin && uid !== senderId && (
                  <div className="flex items-center gap-1">
                    {!isParticipantAdmin && (
                      <button
                        onClick={() => handlePromoteAdmin(uid)}
                        className="text-xs px-2 py-0.5 rounded"
                        style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 10 }}
                        title="Make admin"
                      >
                        Admin
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveMember(uid)}
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ background: 'rgba(248,81,73,0.1)', color: '#f85149', border: '1px solid rgba(248,81,73,0.2)', cursor: 'pointer', fontSize: 10 }}
                      title="Remove from conversation"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Add participant */}
        {isAdmin && (
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <input
              type="text"
              value={addName}
              onChange={e => { setAddName(e.target.value); setShowSuggestions(true) }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Add participant..."
              disabled={addLoading}
              style={inputStyle}
            />
            {showSuggestions && addName.length > 0 && suggestions.length > 0 && (
              <div
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60,
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  borderRadius: 4, marginTop: 2, maxHeight: 200, overflowY: 'auto',
                }}
              >
                {suggestions.map(s => (
                  <div
                    key={s.user_id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                    style={{ fontSize: 12, color: 'var(--text-primary)' }}
                    onMouseDown={() => handleAddMember(s.user_id)}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <PersonaAvatar name={s.user_id} size={18} />
                    <span>{s.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '12px 0' }} />
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Conversation Rules</h3>

        <label style={labelStyle}>Lead</label>
        {(() => {
          // Render-hardening: the lead is authoritative from metadata_lead, but the
          // options are built from participants. Two federation failure modes make the
          // stored lead not literally match any option value:
          //   (a) forward-drop — the lead is genuinely absent from participants;
          //   (b) name<->pu encoding mismatch — the lead IS a participant, but stored
          //       under a different representation (e.g. lead="harbor" while the row is
          //       "pu-...933"), so a string-equal <option> match fails.
          // For (b) the member must RESOLVE and render as the member it is — NOT fall
          // through to the "not a listed member" fallback (that renders a real member as
          // a stranger). allPersonas ({name,user_id}, already fetched) bridges name<->pu.
          const resolveKey = (v?: string) => {
            if (!v) return ''
            const lc = String(v).toLowerCase()
            const hit = allPersonas.find(p => p.name?.toLowerCase() === lc || p.user_id?.toLowerCase() === lc)
            return hit ? hit.user_id.toLowerCase() : lc
          }
          const leadKey = resolveKey(lead)
          // Which participant (if any) IS the current lead, across name<->pu encodings?
          const leadParticipant = lead
            ? participants.find(p =>
                resolveKey(p.persona) === leadKey ||
                resolveKey(p.user_id) === leadKey ||
                resolveKey(p.persona || p.user_id || '') === leadKey)
            : undefined
          const leadCovered = !lead || !!leadParticipant
          return (
            <select value={lead} onChange={e => setLead(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }}>
              <option value="">None</option>
              {/* Mode (a) safety net: lead genuinely absent — keep it selectable, never drop it silently on Save. */}
              {!leadCovered && (
                <option value={lead}>{displayName(lead)} — current lead (not a listed member)</option>
              )}
              {participants.map(p => {
                const pv = p.persona || p.user_id || ''
                // Mode (b): if this participant IS the lead under a different encoding,
                // give its option the STORED lead value so the select matches (shows the
                // member selected) and Save preserves the stored metadata_lead unchanged.
                const isLeadRow = !!leadParticipant && p.user_id === leadParticipant.user_id
                return (
                  <option key={p.user_id} value={isLeadRow ? lead : pv}>{displayName(pv, p.display_name)}</option>
                )
              })}
            </select>
          )
        })()}

        <label style={labelStyle}>Protocol</label>
        <select value={protocol} onChange={e => setProtocol(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }}>
          <option value="open-floor">Open Floor</option>
          <option value="synthesize">Synthesize (lead summarizes, others add only if new)</option>
          <option value="round-robin">Round Robin</option>
          <option value="ceremony">Ceremony</option>
        </select>

        <label style={labelStyle}>Special Instructions</label>
        <textarea
          value={special}
          onChange={e => setSpecial(e.target.value)}
          rows={3}
          placeholder="Custom rules for this conversation..."
          style={{ ...inputStyle, marginBottom: 12, resize: 'vertical', fontFamily: 'inherit' }}
        />

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '12px 0' }} />
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Voice</h3>

        <div className="flex items-center justify-between mb-3">
          <label style={{ ...labelStyle, margin: 0 }}>Chatter (voice presence)</label>
          <button
            onClick={() => setChatter(!chatter)}
            className="text-xs px-3 py-1 rounded-full"
            style={{
              background: chatter ? 'rgba(63,185,80,0.15)' : 'var(--bg-elevated)',
              color: chatter ? 'var(--success)' : 'var(--text-muted)',
              border: chatter ? '1px solid rgba(63,185,80,0.3)' : '1px solid var(--border)',
              cursor: 'pointer',
            }}
          >
            {chatter ? 'ON' : 'OFF'}
          </button>
        </div>

        {error && (
          <div className="text-xs mt-3 px-3 py-2 rounded" style={{ background: 'rgba(248,81,73,0.12)', color: '#f85149', border: '1px solid rgba(248,81,73,0.35)' }}>
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="text-xs px-4 py-1.5 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="text-xs px-4 py-1.5 rounded font-medium" style={{ background: 'var(--success)', color: '#fff', border: 'none', cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════════
// Ceremony Content — two-panel layout
// ═══════════════════════════════════════════════════════════════

function CeremonyContent({
  convo,
  participants,
  conversationId,
  awakeSet,
  grouped,
  messagesEndRef,
  scrollContainerRef,
  handleScroll,
  autoScroll,
  newMsgCount,
  scrollToBottom,
  inputRef,
  draft,
  handleDraftChange,
  handleKeyDown,
  handleSend,
  sendMut,
  chatterEnabled,
  chatterDraft,
  setChatterDraft,
  showChatterInput,
  setShowChatterInput,
  hoverProps,
  handlePaste,
  uploading,
}: {
  convo: Conversation & { metadata_special?: string }
  participants: Participant[]
  conversationId: string
  awakeSet: Set<string>
  grouped: GroupedMessage[]
  messagesEndRef: React.RefObject<HTMLDivElement | null>
  scrollContainerRef: React.RefObject<HTMLDivElement | null>
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void
  autoScroll: boolean
  newMsgCount: number
  scrollToBottom: () => void
  inputRef: React.RefObject<HTMLTextAreaElement | null>
  draft: string
  handleDraftChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  handleKeyDown: (e: React.KeyboardEvent) => void
  handleSend: () => void
  sendMut: { isPending: boolean }
  chatterEnabled: boolean
  chatterDraft: string
  setChatterDraft: (v: string) => void
  showChatterInput: boolean
  setShowChatterInput: (v: boolean) => void
  hoverProps: (name: string) => { onMouseEnter: (e: React.MouseEvent) => void; onMouseLeave: () => void; style: React.CSSProperties }
  handlePaste: (e: React.ClipboardEvent) => void
  uploading: boolean
}) {
  const sseOk = useSSEConnected()
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

  const { data: magicBoxData } = useQuery({
    queryKey: ['magicBox', conversationId],
    queryFn: async () => {
      const res = await fetch(`/api/conversations/${conversationId}/magic-box`, { headers })
      if (!res.ok) return null
      return res.json() as Promise<{ items: MagicBoxItem[]; current: MagicBoxItem | null }>
    },
    enabled: !!conversationId,
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  const magicBoxItems = magicBoxData?.items || []

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* LEFT: Ceremony Stage (40%) */}
      <div
        style={{
          width: '40%',
          flexShrink: 0,
          position: 'relative',
          borderRight: `1px solid rgba(212,160,84,0.15)`,
        }}
      >
        {/* Dark overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
        <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>
          <CeremonyStage
            convo={convo}
            participants={participants}
            magicBoxItems={magicBoxItems}
            awakeSet={awakeSet}
          />
        </div>
      </div>

      {/* RIGHT: Conversation Feed (60%) */}
      <div style={{ width: '60%' }} className="flex flex-col min-w-0 relative overflow-hidden">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto"
          style={{ padding: '16px 0' }}
        >
          <div className="max-w-5xl mx-auto px-5">
            {grouped.map((msg) => (
              <div key={msg.id}>
                {/* Day divider */}
                {msg.dayDivider && (
                  <div className="flex items-center gap-4 my-6">
                    <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                    <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {msg.dayDivider}
                    </span>
                    <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                  </div>
                )}

                {/* System message */}
                {msg.is_system ? (
                  <div className="text-center py-2 my-1">
                    <span
                      className="text-xs italic px-3 py-1 rounded-full"
                      style={{ color: 'var(--text-muted)', background: 'var(--bg-elevated)' }}
                    >
                      {typeof msg.content === 'string' ? msg.content : String(msg.content ?? '')}
                    </span>
                  </div>
                ) : (
                  /* Regular message */
                  <div
                    className="flex gap-3.5 rounded-lg transition-colors"
                    style={{
                      paddingTop: msg.showHeader ? 12 : 2,
                      paddingBottom: 2,
                      paddingLeft: 8,
                      paddingRight: 8,
                      marginTop: msg.showHeader ? 4 : 0,
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = 'var(--bg-elevated)'
                      const ts = e.currentTarget.querySelector('.hover-timestamp') as HTMLElement
                      if (ts) ts.style.opacity = '1'
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = 'transparent'
                      const ts = e.currentTarget.querySelector('.hover-timestamp') as HTMLElement
                      if (ts) ts.style.opacity = '0'
                    }}
                  >
                    {/* Avatar column */}
                    <div className="shrink-0 flex items-start justify-end" style={{ width: 40 }}>
                      {msg.showAvatar ? (
                        <PersonaAvatar
                          name={msg.sender}
                          size={40}
                          showStatus
                          awake={checkAwake(awakeSet, msg.sender)}
                          {...hoverProps(msg.sender)}
                        />
                      ) : (
                        <span
                          className="hover-timestamp text-right"
                          style={{
                            fontSize: 11,
                            color: 'var(--text-muted)',
                            opacity: 0,
                            width: 40,
                            paddingTop: 2,
                            userSelect: 'none',
                          }}
                        >
                          {formatTime(msg.created_at)}
                        </span>
                      )}
                    </div>

                    {/* Message body */}
                    <div className="flex-1 min-w-0">
                      {msg.showHeader && (
                        <div className="flex items-baseline gap-2 mb-1">
                          <span
                            className="text-sm font-semibold"
                            {...hoverProps(msg.sender)}
                            style={{ color: senderColor(msg.sender), cursor: 'pointer' }}
                          >
                            {displayName(msg.sender)}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {formatTime(msg.created_at)}
                          </span>
                        </div>
                      )}
                      <div
                        className="leading-relaxed"
                        style={{
                          color: 'var(--text-primary)',
                          fontSize: 14,
                          lineHeight: 1.5,
                        }}
                      >
                        <MarkdownRenderer content={typeof msg.content === 'string' ? msg.content : String(msg.content ?? '')} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Scroll-to-bottom button */}
        {!autoScroll && newMsgCount > 0 && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-20 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-xs font-medium z-10"
            style={{
              background: 'var(--accent)',
              color: '#fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              border: '1px solid var(--border)',
            }}
          >
            {newMsgCount} new message{newMsgCount > 1 ? 's' : ''} ↓
          </button>
        )}

        {/* Compose bar */}
        <div
          className="shrink-0 px-5 py-3"
          style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border)' }}
        >
          <div className="max-w-5xl mx-auto">
            {/* Chatter input — collapsible, only when chatter enabled */}
            {chatterEnabled && (
              <div className="mb-2">
                {!showChatterInput ? (
                  <button
                    onClick={() => setShowChatterInput(true)}
                    className="text-xs px-2 py-1 rounded-md transition-colors"
                    style={{ color: 'var(--text-muted)', background: 'transparent' }}
                  >
                    💬 Add chatter
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>💬 Chatter</span>
                      <button
                        onClick={() => { setShowChatterInput(false); setChatterDraft('') }}
                        className="text-xs px-1.5 py-0.5 rounded transition-colors"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        ✕
                      </button>
                      {chatterDraft.length > 0 && (
                        <span className="text-xs ml-auto" style={{
                          color: chatterDraft.length > 250 ? '#f85149' : chatterDraft.length > 160 ? '#d29922' : 'var(--text-muted)',
                        }}>
                          {chatterDraft.length}{chatterDraft.length > 250 ? ' — too long' : chatterDraft.length > 160 ? ' — getting long' : ''}
                        </span>
                      )}
                    </div>
                    <input
                      type="text"
                      value={chatterDraft}
                      onChange={(e) => setChatterDraft(e.target.value)}
                      placeholder="Optional — one or two sentences for Joel's ears and glance"
                      className="w-full px-3 py-1.5 rounded-lg text-xs outline-none"
                      style={{
                        background: 'var(--bg-body)',
                        color: 'var(--text-primary)',
                        border: `1px solid ${chatterDraft.length > 250 ? '#f85149' : chatterDraft.length > 160 ? '#d29922' : 'var(--border)'}`,
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3 items-end">
              <textarea
                ref={inputRef}
                value={draft}
                onChange={handleDraftChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={uploading ? 'Uploading screenshot…' : 'Type a message…'}
                rows={3}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm outline-none resize-none"
                style={{
                  background: 'var(--bg-body)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border)',
                  maxHeight: 120,
                  lineHeight: 1.5,
                }}
              />
              <button
                onClick={handleSend}
                disabled={!draft.trim() || sendMut.isPending || uploading}
                className="px-5 py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 shrink-0 transition-colors"
                style={{
                  background: draft.trim() ? 'var(--accent)' : 'var(--bg-elevated)',
                  color: draft.trim() ? '#fff' : 'var(--text-muted)',
                  border: '1px solid var(--border)',
                }}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════

export default function ConversationViewPage({ overrideId }: { overrideId?: string } = {}) {
  const params = useParams<{ id: string }>()
  const id = overrideId || params.id
  const { user } = useAuth()
  const sseOk = useSSEConnected()
  const { hoverProps } = usePersonaPopup()
  const senderId = user?.userId || user?.loginName || user?.userName || 'joel'
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)
  const [newMsgCount, setNewMsgCount] = useState(0)
  const [draft, setDraft] = useState(() => id ? localStorage.getItem(`draft-${id}`) || '' : '')
  const [showMagicBox, _setShowMagicBox] = useState(() => {
    if (!id) return false
    const saved = sessionStorage.getItem(`mb-vis-${id}`)
    return saved === '1'
  })
  const setShowMagicBox = (val: boolean) => {
    _setShowMagicBox(val)
    if (id) sessionStorage.setItem(`mb-vis-${id}`, val ? '1' : '0')
  }
  // Reset visibility when navigating between conversations (useState initializer only runs once)
  useEffect(() => {
    if (!id) return
    const saved = sessionStorage.getItem(`mb-vis-${id}`)
    _setShowMagicBox(saved === '1')
  }, [id])
  const [showSettings, setShowSettings] = useState(false)
  const [showParticipants, setShowParticipants] = useState(false)
  const [showVideo, setShowVideo] = useState(false)
  const [tableMode, _setTableMode] = useState<'convo' | 'agenda' | 'triage' | 'emergency' | 'present'>(() => {
    if (!id) return 'convo'
    const saved = localStorage.getItem(`table-mode-${id}`)
    return (saved === 'agenda' || saved === 'triage' || saved === 'present') ? saved : 'convo'
  })
  const setTableMode = useCallback((mode: 'convo' | 'agenda' | 'triage' | 'emergency' | 'present') => {
    _setTableMode(mode)
    if (id) localStorage.setItem(`table-mode-${id}`, mode)
    if (mode === 'emergency') {
      const tableMembers = ['mira', 'griot', 'tack', 'forge', 'flux', 'vector', 'sentinel', 'pulse']
      for (const p of tableMembers) {
        fetch(`http://localhost:5055/api/ops/queue/${p}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: 'EMERGENCY — Report to The Round Table immediately. Joel has activated Emergency Mode.', from: 'joel', priority: 3 }),
        }).catch(() => {})
      }
    }
  }, [])

  // Jitsi room name for video calls
  const jitsiRoomName = id ? `gathering-${id.replace(/[^a-zA-Z0-9]/g, '')}` : ''
  const inputRef = useRef<HTMLTextAreaElement>(null)
  // Synchronous guard against double-send — sendMut.isPending is async (needs re-render),
  // so rapid Enter presses can sneak through before React updates. This ref flips immediately.
  const sendingRef = useRef(false)

  // Persist draft to localStorage on change
  useEffect(() => {
    if (id) localStorage.setItem(`draft-${id}`, draft)
  }, [draft, id])

  // Mark conversation as read on mount + when new messages arrive
  useEffect(() => {
    if (!id) return
    const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
    fetch(`/api/conversations/${id}/read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    }).catch(() => {})
  }, [id])

  const queryClient = useQueryClient()
  const awakeSet = useAwakePersonas()

  const { data: allPersonas } = useQuery({
    queryKey: ['allPersonas'],
    queryFn: async () => {
      const res = await fetch('/api/personas', { headers: (() => { const t = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]; return t ? { Authorization: `Bearer ${t}` } : {} })() as Record<string, string> })
      if (!res.ok) return []
      return res.json() as Promise<{ name: string; display_name?: string; user_id?: string }[]>
    },
  })

  const { data: convo } = useQuery({
    queryKey: ['conversation', id],
    queryFn: () => fetchConversation(id!),
    enabled: !!id,
    refetchInterval: sseOk ? false : 30_000, // SSE-conditional polling fallback
  })

  const { data: msgData } = useQuery({
    queryKey: ['messages', id],
    queryFn: () => fetchMessages(id!),
    enabled: !!id,
    refetchInterval: sseOk ? false : 15_000, // SSE-conditional polling fallback
  })

  // Fetch persona list for display name resolution (covers senders not in this conversation)
  const { data: personaList } = useQuery<{ name: string; display_name: string; id?: string }[]>({
    queryKey: ['persona-names-for-chat'],
    queryFn: async () => {
      const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1] || ''
      const res = await fetch('/api/personas', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) return []
      return res.json()
    },
    staleTime: 120_000,
  })
  useMemo(() => {
    if (personaList) {
      for (const p of personaList) {
        if (p.id) _displayNameCache[p.id] = p.display_name || p.name
        _displayNameCache[p.name] = p.display_name || p.name
        _displayNameCache[p.name.toLowerCase()] = p.display_name || p.name
      }
    }
  }, [personaList])

  // Fetch ceremony data when room_type is ceremony
  const { data: ceremonyData } = useQuery({
    queryKey: ['ceremony-by-conv', id],
    queryFn: async () => {
      const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
      const res = await fetch(`/api/ceremonies/by-conversation/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) return null
      return res.json() as Promise<{ phase?: string; status?: string; the_word?: string; persona?: string; curandera?: string; type?: string }>
    },
    enabled: !!id && convo?.room_type === 'ceremony',
    refetchInterval: sseOk ? false : 30_000,
  })

  // Fetch project data when room_type is workspace or project_id is set
  const projectId = convo?.project_id
  const { data: projectData } = useQuery({
    queryKey: ['project-with-tasks', projectId],
    queryFn: async () => {
      const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
      const [projRes, tasksRes] = await Promise.all([
        fetch(`/api/projects/${projectId}`, { headers }),
        fetch(`/api/projects/${projectId}/tasks`, { headers }),
      ])
      const proj = projRes.ok ? await projRes.json() : null
      const tasksData = tasksRes.ok ? await tasksRes.json() : null
      if (!proj) return null
      return { ...proj, tasks: tasksData?.tasks || [] }
    },
    enabled: !!projectId && convo?.room_type === 'workspace',
    refetchInterval: sseOk ? false : 60_000,
  })

  const [chatterDraft, setChatterDraft] = useState('')
  const [showChatterInput, setShowChatterInput] = useState(false)

  const sendMut = useMutation({
    mutationFn: (payload: { content: string; chatter?: string }) =>
      sendMessage(id!, payload.content, senderId, payload.chatter),
    // Optimistic clear: empty the composer the instant they hit send, so a slow/jittery
    // connection can't make it look "stuck" (which caused accidental re-sends). The
    // content is captured in the mutation variable, so clearing is safe; restored on error.
    onMutate: () => {
      setDraft('')
      setChatterDraft('')
      setShowChatterInput(false)
      setAutoScroll(true)
    },
    onSuccess: () => {
      sendingRef.current = false
      queryClient.invalidateQueries({ queryKey: ['messages', id] })
      setNewMsgCount(0)
      const doScroll = () => messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' })
      setTimeout(doScroll, 100)
      setTimeout(doScroll, 500)
    },
    onError: (err, payload) => {
      sendingRef.current = false
      // Send failed — restore what they typed so nothing is lost.
      setDraft(payload.content)
      if (payload.chatter) {
        setChatterDraft(payload.chatter)
        setShowChatterInput(true)
      }
      console.error('[Chat] Send failed:', err)
      alert(`Message failed to send: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
  })

  const messages = msgData?.messages || []
  const grouped = useMemo(() => groupMessages(messages), [messages])
  const lastMsgId = messages.length > 0 ? messages[messages.length - 1].id : null
  const prevLastMsgIdRef = useRef<string | null>(null)

  // Auto-scroll on new messages — track by last message ID, not count (count caps at limit=50)
  // Position check: wheel/touch events don't always fire for every scroll source
  // (keyboard, programmatic, screen-reader, inertia). Before auto-scrolling,
  // confirm the user is actually AT the bottom — if they've scrolled up to read
  // history, respect that position and just bump the new-message badge.
  useEffect(() => {
    if (lastMsgId && lastMsgId !== prevLastMsgIdRef.current) {
      if (prevLastMsgIdRef.current !== null) {
        // Not initial load — new message arrived
        const el = scrollContainerRef.current
        const atBottom = el ? el.scrollHeight - el.scrollTop - el.clientHeight < 150 : true
        if (autoScroll && atBottom) {
          const doScroll = () => messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' })
          requestAnimationFrame(doScroll)
          setTimeout(doScroll, 150)
          if (!document.hasFocus()) flashTabTitle(1)
        } else {
          // User is scrolled up reading — don't yank them away
          if (autoScroll && !atBottom) setAutoScroll(false)
          setNewMsgCount(c => {
            const next = c + 1
            flashTabTitle(next)
            return next
          })
        }
      } else {
        // Initial load — scroll to bottom
        requestAnimationFrame(() => messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' }))
      }
      prevLastMsgIdRef.current = lastMsgId
    }
  }, [lastMsgId, autoScroll])

  // Scroll position handler — fires on ALL scroll sources (wheel, touch, scrollbar, keyboard, momentum)
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150
    if (atBottom) {
      setAutoScroll(true)
      setNewMsgCount(0)
    } else {
      setAutoScroll(false)
    }
  }, [])

  // Scroll position tracking is handled by onScroll on the scroll container,
  // which fires for all scroll sources (wheel, touch, scrollbar, keyboard, momentum).

  function scrollToBottom() {
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' })
    setAutoScroll(true)
    setNewMsgCount(0)
  }

  function handleSend() {
    if (!draft.trim()) return
    // Prevent double-send: sendMut.isPending is async (needs React re-render),
    // so rapid Enter presses can sneak through. The ref flips synchronously.
    if (sendMut.isPending || sendingRef.current) return
    sendingRef.current = true
    const chatter = chatterDraft.trim() || undefined
    sendMut.mutate({ content: draft.trim(), chatter })
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const [uploading, setUploading] = useState(false)

  async function handlePaste(e: React.ClipboardEvent) {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of Array.from(items)) {
      if (!item.type.startsWith('image/')) continue
      e.preventDefault()
      const file = item.getAsFile()
      if (!file) continue
      setUploading(true)
      try {
        const formData = new FormData()
        formData.append('file', file, `screenshot-${Date.now()}.png`)
        const res = await fetch('/api/upload', { method: 'POST', body: formData })
        if (!res.ok) throw new Error('Upload failed')
        const data = await res.json()
        const url = data.url || `/uploads/${data.filename}`
        const fullUrl = `${window.location.origin}${url}`
        setDraft(prev => prev + (prev ? '\n' : '') + `![Screenshot](${fullUrl})`)
      } catch (err) {
        console.error('Screenshot upload failed:', err)
        alert('Screenshot upload failed')
      } finally {
        setUploading(false)
      }
      return
    }
  }

  // Auto-resize textarea
  function handleDraftChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setDraft(e.target.value)
    const ta = e.target
    ta.style.height = 'auto'
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px'
  }

  const participants = convo?.participants || []
  // Build display name cache from participants so PU-numbers resolve to names
  useMemo(() => buildDisplayNameCache(participants), [participants])

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--bg-body)' }}>

      {/* ─── Header ─────────────────────────────────────────── */}
      <header
        className="shrink-0 px-5 py-3 flex items-center gap-3"
        style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}
      >
        <button
          onClick={() => window.history.length > 1 ? window.history.back() : window.location.href = '/conversations'}
          className="text-xs no-underline px-2 py-1 rounded"
          style={{ color: 'var(--text-muted)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          ← Back
        </button>

        {/* Participant count — click to expand dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowParticipants(prev => !prev)}
            className="text-xs px-2 py-1 rounded"
            style={{
              color: 'var(--text-secondary)', background: 'var(--bg-elevated)',
              border: '1px solid var(--border)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <span>👥</span>
            <span>{participants.length} participant{participants.length !== 1 ? 's' : ''}</span>
          </button>
          {showParticipants && (() => {
            const tableSeats = new Set(['mira', 'griot', 'tack', 'forge', 'flux', 'vector', 'joel'])
            const permanentMembers = participants.filter(p => tableSeats.has((p.persona || p.user_id || '').toLowerCase()))
            const guests = participants.filter(p => !tableSeats.has((p.persona || p.user_id || '').toLowerCase()))
            const participantIds = new Set(participants.map(p => (p.persona || p.user_id || '').toLowerCase()))
            return (
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 6,
              background: 'var(--bg-primary, #0a0a0f)',
              border: '1px solid var(--border)',
              borderRadius: 10, padding: 8, minWidth: 280, maxHeight: 460,
              overflow: 'auto', zIndex: 100,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}>
              {/* Permanent seats */}
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '4px 8px', marginBottom: 4 }}>
                Table ({permanentMembers.length})
              </div>
              {permanentMembers.map(p => {
                const personaName = p.persona || p.user_id || ''
                const name = displayName(personaName)
                const isAwake = checkAwake(awakeSet, personaName)
                const isLead = convo?.metadata_lead === p.user_id || convo?.metadata_lead === p.persona
                return (
                  <div key={p.user_id || p.persona} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, background: isLead ? 'var(--bg-elevated)' : 'transparent' }}>
                    <PersonaAvatar name={personaName} size={24} showStatus awake={isAwake} />
                    <span className="text-xs" style={{ color: 'var(--text-primary)', fontWeight: isLead ? 600 : 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                    {isLead && <span style={{ fontSize: 12, color: 'var(--accent, #E8A63A)' }}>★</span>}
                    {isAwake && <span style={{ fontSize: 8, color: 'var(--success)' }}>●</span>}
                  </div>
                )
              })}

              {/* Active guests */}
              {guests.length > 0 && (
                <>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '4px 8px', marginTop: 8, marginBottom: 4 }}>
                    Guests ({guests.length})
                  </div>
                  {guests.map(p => {
                    const personaName = p.persona || p.user_id || ''
                    const name = displayName(personaName)
                    const isAwake = checkAwake(awakeSet, personaName)
                    return (
                      <div key={p.user_id || p.persona} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6 }}>
                        <PersonaAvatar name={personaName} size={20} showStatus awake={isAwake} />
                        <span className="text-xs" style={{ color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                        {isAwake && <span style={{ fontSize: 8, color: 'var(--success)' }}>●</span>}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation()
                            const tk = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
                            await fetch(`/api/conversations/${id}/participants/${p.user_id || p.persona}`, {
                              method: 'DELETE', headers: tk ? { Authorization: `Bearer ${tk}` } : {},
                            })
                            queryClient.invalidateQueries({ queryKey: ['conversation', id] })
                          }}
                          style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer' }}
                          title={`Dismiss ${name}`}
                        >Dismiss</button>
                      </div>
                    )
                  })}
                </>
              )}

              {/* All personas — invite section */}
              <details style={{ marginTop: 8 }}>
                <summary style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '4px 8px', cursor: 'pointer', userSelect: 'none' }}>
                  Invite Persona
                </summary>
                <div style={{ maxHeight: 200, overflow: 'auto', marginTop: 4 }}>
                  {(allPersonas || []).filter(p => !participantIds.has(p.name?.toLowerCase())).map(p => (
                    <div
                      key={p.name}
                      onClick={async () => {
                        const tk = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
                        await fetch(`/api/conversations/${id}/participants`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...(tk ? { Authorization: `Bearer ${tk}` } : {}) },
                          body: JSON.stringify({ user_id: p.name }),
                        })
                        queryClient.invalidateQueries({ queryKey: ['conversation', id] })
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 6, cursor: 'pointer', opacity: 0.5 }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'var(--bg-elevated)' }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.background = 'transparent' }}
                    >
                      <PersonaAvatar name={p.name} size={20} />
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.display_name || p.name}</span>
                      <span style={{ fontSize: 9, color: 'var(--text-muted)', marginLeft: 'auto' }}>+ Invite</span>
                    </div>
                  ))}
                </div>
              </details>
            </div>
            )
          })()}
        </div>

        {(() => {
          // 1:1 conversations always render as "{Persona} / {Viewer}" and are
          // not renameable — the relationship IS the name. Stored titles (like
          // "Flux Home") are ignored for 1:1s to prevent old custom titles from
          // making the header look like a page/profile route.
          const isOneOnOne = convo?.type === '1:1' && (participants || []).length <= 2
          if (isOneOnOne) {
            const viewerName = user?.loginName || user?.userName || 'You'
            const selfIds = [viewerName.toLowerCase(), user?.userId?.toLowerCase(), 'joel'].filter(Boolean) as string[]
            const other = (participants || []).find(p => {
              const id = (p.persona || p.user_id || p.display_name || '').toLowerCase()
              return !selfIds.includes(id)
            })
            const personaLabel = displayName(other?.persona || other?.display_name || other?.user_id || 'Persona')
            const viewerLabel = displayName(viewerName)
            return (
              <h1
                className="text-sm font-semibold truncate flex-1"
                style={{ color: 'var(--text-primary)' }}
                title="1:1 — not renameable"
              >
                {personaLabel} / {viewerLabel}
              </h1>
            )
          }
          return (
            <h1
              className="text-sm font-semibold truncate flex-1"
              style={{ color: 'var(--text-primary)', cursor: 'pointer' }}
              title="Click to edit title"
              onClick={() => {
                const newTitle = prompt('Conversation title:', convo?.title || '')
                if (newTitle !== null) {
                  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
                  fetch(`/api/conversations/${id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    body: JSON.stringify({ title: newTitle.trim() || null }),
                  }).then(() => queryClient.invalidateQueries({ queryKey: ['conversation', id] }))
                }
              }}
            >
              {String(convo?.title || '')}
            </h1>
          )
        })()}

        {/* Clean action buttons — only essentials in header */}
        <div className="flex items-center gap-2">
          {convo?.metadata_lead && typeof convo.metadata_lead === 'string' && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
              Lead: {displayName(convo.metadata_lead)}
            </span>
          )}
          {(convo?.room_type === 'round-table' || convo?.room_type === 'circle' || convo?.room_type === 'workspace') && (
            <div className="flex items-center rounded-full overflow-hidden" style={{ border: '1px solid var(--border)' }}>
              <button
                onClick={() => setTableMode('convo')}
                className="text-xs px-2.5 py-0.5"
                style={{
                  background: tableMode === 'convo' ? '#8B5E3C' : 'transparent',
                  color: tableMode === 'convo' ? '#fff' : 'var(--text-muted)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                Convo
              </button>
              <button
                onClick={() => setTableMode('agenda')}
                className="text-xs px-2.5 py-0.5"
                style={{
                  background: tableMode === 'agenda' ? '#8B5E3C' : 'transparent',
                  color: tableMode === 'agenda' ? '#fff' : 'var(--text-muted)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                Agenda
              </button>
              <button
                onClick={() => setTableMode('triage')}
                className="text-xs px-2.5 py-0.5"
                style={{
                  background: tableMode === 'triage' ? '#8B5E3C' : 'transparent',
                  color: tableMode === 'triage' ? '#fff' : 'var(--text-muted)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                Triage
              </button>
              <button
                onClick={() => setTableMode(tableMode === 'emergency' ? 'convo' : 'emergency')}
                className="text-xs px-2.5 py-0.5"
                style={{
                  background: tableMode === 'emergency' ? '#dc2626' : 'transparent',
                  color: tableMode === 'emergency' ? '#fff' : 'var(--text-muted)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                {tableMode === 'emergency' ? '🚨' : ''} Emergency
              </button>
              <button
                onClick={() => setTableMode(tableMode === 'present' ? 'convo' : 'present')}
                className="text-xs px-2.5 py-0.5 relative"
                style={{
                  background: tableMode === 'present' ? '#8B5E3C' : 'transparent',
                  color: tableMode === 'present' ? '#fff' : 'var(--text-muted)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                Present
              </button>
            </div>
          )}
          {convo?.metadata_chatter ? (
            <span className="text-xs" style={{ color: 'var(--success)' }} title="Chatter is ON">🔊</span>
          ) : null}
          <button
            onClick={() => setShowVideo(v => !v)}
            className="text-xs px-1.5 py-1 rounded"
            style={{
              color: showVideo ? '#34d399' : 'var(--text-muted)',
              background: showVideo ? 'rgba(52,211,153,0.12)' : 'transparent',
              border: 'none', cursor: 'pointer',
            }}
            title={showVideo ? 'End video call' : 'Start video call'}
          >
            📹
          </button>
          <button
            onClick={() => setShowSettings(s => !s)}
            className="px-1.5 py-1 rounded"
            style={{ color: 'var(--text-muted)', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}
            title="Settings"
          >
            ⚙️
          </button>
        </div>
      </header>

      {/* ─── Emergency Banner ───────────────────────────────── */}
      {tableMode === 'emergency' && (
        <div
          className="shrink-0 px-5 py-3 flex items-center gap-3"
          style={{
            background: 'linear-gradient(135deg, rgba(220,38,38,0.15), rgba(220,38,38,0.05))',
            borderBottom: '2px solid #dc2626',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        >
          <span style={{ fontSize: 18 }}>🚨</span>
          <div className="flex-1">
            <span className="text-sm font-bold" style={{ color: '#dc2626' }}>EMERGENCY SESSION</span>
            <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>All Table members have been notified. Switch back to end.</span>
          </div>
        </div>
      )}

      {/* ─── Workspace Banner ───────────────────────────────── */}
      {convo?.room_type === 'workspace' && projectData && (
        <div
          className="shrink-0 px-5 py-2.5 flex items-center gap-3"
          style={{
            background: 'linear-gradient(135deg, rgba(88,166,255,0.08), rgba(88,166,255,0.02))',
            borderBottom: '1px solid rgba(88,166,255,0.2)',
          }}
        >
          <span style={{ fontSize: 14, color: WORKSPACE_ACCENT }}>⚡</span>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold" style={{ color: WORKSPACE_ACCENT }}>
              {projectData.title || 'Workspace'}
            </span>
            {projectData.status && (
              <span className="text-xs ml-2 px-1.5 py-0.5 rounded" style={{ background: 'rgba(88,166,255,0.1)', color: 'var(--text-muted)' }}>
                {projectData.status}
              </span>
            )}
          </div>
          {projectData.tasks && (
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {projectData.tasks.filter((t: ProjectTask) => t.status === 'completed').length}/{projectData.tasks.length} tasks
            </span>
          )}
        </div>
      )}

      {/* ─── Ceremony Banner ────────────────────────────────── */}
      {convo?.room_type === 'ceremony' && (
        <div
          className="shrink-0 px-5 py-3 flex items-center gap-4"
          style={{
            background: 'linear-gradient(135deg, rgba(212,160,84,0.1), rgba(212,160,84,0.02))',
            borderBottom: '1px solid rgba(212,160,84,0.2)',
          }}
        >
          <span style={{ fontSize: 20 }}>🔥</span>
          <div className="flex-1">
            <div className="text-sm font-semibold" style={{ color: '#D4A054' }}>
              {String(convo.title || 'Ceremony Room')}
            </div>
            {convo.metadata_special && typeof convo.metadata_special === 'string' && (
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                {String(convo.metadata_special)}
              </div>
            )}
          </div>
          {convo.metadata_lead && typeof convo.metadata_lead === 'string' && (
            <div className="flex items-center gap-1.5">
              <PersonaAvatar name={convo.metadata_lead} size={18} />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Guide</span>
            </div>
          )}
        </div>
      )}

      {/* ─── Ceremony Phase Indicator ─────────────────────── */}
      {convo?.room_type === 'ceremony' && (
        <CeremonyPhaseBar
          currentPhase={ceremonyData?.phase
            ? ceremonyData.phase.charAt(0).toUpperCase() + ceremonyData.phase.slice(1)
            : ceremonyData?.status === 'completed' ? 'Integration' : 'Preparing'}
        />
      )}

      {/* ─── Settings Panel Overlay ────────────────────────── */}
      {showSettings && (
        <SettingsPanel
          convo={convo}
          convId={id!}
          participants={participants}
          senderId={senderId}
          onClose={() => setShowSettings(false)}
          onSave={() => {
            queryClient.invalidateQueries({ queryKey: ['conversation', id] })
            setShowSettings(false)
          }}
        />
      )}

      {/* ─── Content area ─────────────────────────────────── */}
      {convo?.room_type === 'ceremony' ? (
        <CeremonyContent
          convo={convo}
          participants={participants}
          conversationId={id!}
          awakeSet={awakeSet}
          grouped={grouped}
          messagesEndRef={messagesEndRef}
          scrollContainerRef={scrollContainerRef}
          handleScroll={handleScroll}
          autoScroll={autoScroll}
          newMsgCount={newMsgCount}
          scrollToBottom={scrollToBottom}
          inputRef={inputRef}
          draft={draft}
          handleDraftChange={handleDraftChange}
          handleKeyDown={handleKeyDown}
          handleSend={handleSend}
          sendMut={sendMut}
          chatterEnabled={!!convo?.metadata_chatter}
          chatterDraft={chatterDraft}
          setChatterDraft={setChatterDraft}
          showChatterInput={showChatterInput}
          setShowChatterInput={setShowChatterInput}
          hoverProps={hoverProps}
          handlePaste={handlePaste}
          uploading={uploading}
        />
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex overflow-hidden">

          {/* Workspace sidebar — project tasks panel */}
          {convo?.room_type === 'workspace' && projectId && (
            <WorkspaceSidebar projectData={projectData} awakeSet={awakeSet} />
          )}

          {/* Video Call Panel */}
          {showVideo && jitsiRoomName && (
            <div
              style={{
                width: '40%',
                flexShrink: 0,
                borderRight: '1px solid var(--border)',
                background: '#000',
                position: 'relative',
              }}
            >
              <iframe
                src={`https://meet.thegathering.space/${jitsiRoomName}#config.prejoinConfig.enabled=false&config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=false&config.enableLobbyChat=false&config.requireDisplayName=false&config.lobbyModeEnabled=false&config.disableThirdPartyRequests=true&config.disableProfile=true&config.filmstrip.disabled=true&config.participantsPane.enabled=false&config.notifications=[]&interfaceConfig.SHOW_JITSI_WATERMARK=false&interfaceConfig.FILM_STRIP_MAX_HEIGHT=0&interfaceConfig.VERTICAL_FILMSTRIP=false&interfaceConfig.TOOLBAR_BUTTONS=["microphone","camera","desktop","raisehand","hangup"]&userInfo.displayName=${encodeURIComponent(user?.loginName || user?.userName || 'Guest')}`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="camera; microphone; fullscreen; display-capture; autoplay"
                allowFullScreen
              />
            </div>
          )}

          {/* Left panel — Project Browser in Triage mode, Magic Box otherwise */}
          {id && tableMode === 'present' && (convo?.room_type === 'round-table' || convo?.room_type === 'circle' || convo?.room_type === 'workspace') ? (
            <Suspense fallback={<div style={{ width: 380, flexShrink: 0, borderRight: '1px solid var(--border)' }} />}>
              <PresentationBrowser conversationId={id} />
            </Suspense>
          ) : id && tableMode === 'triage' && (convo?.room_type === 'round-table' || convo?.room_type === 'circle' || convo?.room_type === 'workspace') ? (
            <Suspense fallback={<div style={{ width: 380, flexShrink: 0, borderRight: '1px solid var(--border)' }} />}>
              <ProjectBrowser />
            </Suspense>
          ) : id && showMagicBox ? (
            <MagicBoxErrorBoundary>
              <MagicBox
                conversationId={id}
                onClose={() => setShowMagicBox(false)}
              />
            </MagicBoxErrorBoundary>
          ) : id && !showMagicBox ? (
            <div
              onClick={() => setShowMagicBox(true)}
              title="Open Magic Box"
              style={{
                width: 24,
                flexShrink: 0,
                cursor: 'pointer',
                background: 'var(--bg-elevated)',
                borderRight: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                writingMode: 'vertical-rl',
                fontSize: 11,
                color: 'var(--text-muted)',
                letterSpacing: 1,
                userSelect: 'none',
              }}
            >
              ✦ Magic Box
            </div>
          ) : null}

          {/* Messages area */}
          <div className="flex-1 flex flex-col min-w-0 relative overflow-hidden">
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto"
              style={{ padding: '16px 0' }}
            >
              <div className="max-w-5xl mx-auto px-5">
                {grouped.map((msg) => (
                  <div key={msg.id} data-msg-id={msg.id}>
                    {/* Day divider */}
                    {msg.dayDivider && (
                      <div className="flex items-center gap-4 my-6">
                        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                        <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                          {msg.dayDivider}
                        </span>
                        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                      </div>
                    )}

                    {/* System message */}
                    {msg.is_system ? (
                      <div className="text-center py-2 my-1">
                        <span
                          className="text-xs italic px-3 py-1 rounded-full"
                          style={{ color: 'var(--text-muted)', background: 'var(--bg-elevated)' }}
                        >
                          {typeof msg.content === 'string' ? msg.content : String(msg.content ?? '')}
                        </span>
                      </div>
                    ) : (
                      /* Regular message */
                      <div
                        className="flex gap-3.5 rounded-lg transition-colors"
                        style={{
                          paddingTop: msg.showHeader ? 12 : 2,
                          paddingBottom: 2,
                          paddingLeft: 8,
                          paddingRight: 8,
                          marginTop: msg.showHeader ? 4 : 0,
                        }}
                        onMouseOver={e => {
                          e.currentTarget.style.background = 'var(--bg-elevated)'
                          const ts = e.currentTarget.querySelector('.hover-timestamp') as HTMLElement
                          if (ts) ts.style.opacity = '1'
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.background = 'transparent'
                          const ts = e.currentTarget.querySelector('.hover-timestamp') as HTMLElement
                          if (ts) ts.style.opacity = '0'
                        }}
                      >
                        {/* Avatar column — shows avatar or hover-reveal timestamp */}
                        <div className="shrink-0 flex items-start justify-end" style={{ width: 40 }}>
                          {msg.showAvatar ? (
                            <PersonaAvatar
                              name={msg.sender}
                              size={40}
                              showStatus
                              awake={checkAwake(awakeSet, msg.sender)}
                              {...hoverProps(msg.sender)}
                            />
                          ) : (
                            <span
                              className="hover-timestamp text-right"
                              style={{
                                fontSize: 11,
                                color: 'var(--text-muted)',
                                opacity: 0,
                                width: 40,
                                paddingTop: 2,
                                userSelect: 'none',
                              }}
                            >
                              {formatTime(msg.created_at)}
                            </span>
                          )}
                        </div>

                        {/* Message body */}
                        <div className="flex-1 min-w-0">
                          {msg.showHeader && (
                            <div className="flex items-baseline gap-2 mb-1">
                              <span
                                className="text-sm font-semibold"
                                {...hoverProps(msg.sender)}
                                style={{ color: senderColor(msg.sender), cursor: 'pointer' }}
                              >
                                {displayName(msg.sender)}
                              </span>
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                {formatTime(msg.created_at)}
                              </span>
                            </div>
                          )}
                          <div
                            className="leading-relaxed"
                            style={{
                              color: 'var(--text-primary)',
                              fontSize: 14,
                              lineHeight: 1.5,
                            }}
                          >
                            <MarkdownRenderer content={typeof msg.content === 'string' ? msg.content : String(msg.content ?? '')} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Scroll-to-bottom button */}
            {!autoScroll && newMsgCount > 0 && (
              <button
                onClick={scrollToBottom}
                className="absolute bottom-20 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-xs font-medium z-10"
                style={{
                  background: 'var(--accent)',
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                  border: '1px solid var(--border)',
                }}
              >
                {newMsgCount} new message{newMsgCount > 1 ? 's' : ''} ↓
              </button>
            )}

          </div>

          {/* Right panel — Dashboard (Round Table or Workspace) OR Chatter Box */}
          {id && (convo?.room_type === 'round-table' || convo?.room_type === 'circle' || convo?.room_type === 'workspace') ? (
            <RightPanelTabs conversationId={id}>
              <Suspense fallback={<div style={{ width: '100%', height: '100%' }} />}>
                <RoundTableDashboard
                  conversationId={id}
                  tableMode={tableMode}
                  title={(convo?.room_type === 'workspace') ? (projectData?.title || 'Project') : undefined}
                  seatOverrides={undefined}
                />
              </Suspense>
            </RightPanelTabs>
          ) : id && !!convo?.metadata_chatter ? (
            <ChatterBox
              conversationId={id}
              onScrollToMessage={(msgId) => {
                const el = scrollContainerRef.current?.querySelector(`[data-msg-id="${msgId}"]`)
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  const div = el as HTMLElement
                  div.style.background = 'var(--bg-elevated)'
                  setTimeout(() => { div.style.background = '' }, 2000)
                }
              }}
            />
          ) : null}
        </div>

          {/* ─── Compose bar — full width below panels ─────────── */}
          <div
            className="shrink-0 px-5 py-3"
            style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border)' }}
          >
            <div className="max-w-5xl mx-auto">
              {/* Chatter input — collapsible, only when chatter enabled */}
              {!!convo?.metadata_chatter && (
                <div className="mb-2">
                  {!showChatterInput ? (
                    <button
                      onClick={() => setShowChatterInput(true)}
                      className="text-xs px-2 py-1 rounded-md transition-colors"
                      style={{ color: 'var(--text-muted)', background: 'transparent' }}
                    >
                      💬 Add chatter
                    </button>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>💬 Chatter</span>
                        <button
                          onClick={() => { setShowChatterInput(false); setChatterDraft('') }}
                          className="text-xs px-1.5 py-0.5 rounded transition-colors"
                          style={{ color: 'var(--text-muted)' }}
                        >
                          ✕
                        </button>
                        {chatterDraft.length > 0 && (
                          <span className="text-xs ml-auto" style={{
                            color: chatterDraft.length > 250 ? '#f85149' : chatterDraft.length > 160 ? '#d29922' : 'var(--text-muted)',
                          }}>
                            {chatterDraft.length}{chatterDraft.length > 250 ? ' — too long' : chatterDraft.length > 160 ? ' — getting long' : ''}
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        value={chatterDraft}
                        onChange={(e) => setChatterDraft(e.target.value)}
                        placeholder="Optional — one or two sentences for Joel's ears and glance"
                        className="w-full px-3 py-1.5 rounded-lg text-xs outline-none"
                        style={{
                          background: 'var(--bg-body)',
                          color: 'var(--text-primary)',
                          border: `1px solid ${chatterDraft.length > 250 ? '#f85149' : chatterDraft.length > 160 ? '#d29922' : 'var(--border)'}`,
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-3 items-end">
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={handleDraftChange}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  placeholder={uploading ? 'Uploading screenshot…' : 'Type a message…'}
                  rows={3}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm outline-none resize-none"
                  style={{
                    background: 'var(--bg-body)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border)',
                    maxHeight: 120,
                    lineHeight: 1.5,
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={!draft.trim() || sendMut.isPending || uploading}
                  className="px-5 py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 shrink-0 transition-colors"
                  style={{
                    background: draft.trim() ? 'var(--accent)' : 'var(--bg-elevated)',
                    color: draft.trim() ? '#fff' : 'var(--text-muted)',
                    border: '1px solid var(--border)',
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chatter audio player — polls and auto-plays voice clips */}
      {id && !!convo?.metadata_chatter && (
        <MagicBoxErrorBoundary>
          <ChatterPlayer conversationId={id} chatterEnabled={true} />
        </MagicBoxErrorBoundary>
      )}
    </div>
  )
}
